// src/components/Dashboard.tsx

import { useState, useEffect } from 'react';
import type { Stats, TopIp, DatabaseStatus } from '../types';
import apiService from '../services/api'; // Now using real API

interface DashboardProps {
  stats: Stats;
}

function Dashboard({ stats }: DashboardProps) {
  const [topIps, setTopIps] = useState<TopIp[]>([]);
  const [dbStatus, setDbStatus] = useState<DatabaseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load data from real API
      const [topIpsData, dbStatusData] = await Promise.all([
        apiService.getTopIps(24),
        apiService.getDatabaseStatus(),
      ]);
      
      setTopIps(topIpsData);
      setDbStatus(dbStatusData);
    } catch (err: any) {
      console.error('Error loading dashboard data:', err);
      setError('Failed to load dashboard data. Make sure IpInfo API is running.');
    } finally {
      setLoading(false);
    }
  };

  const getChangeIndicator = (value: number, threshold: number, reverse = false) => {
    const isPositive = reverse ? value < threshold : value > threshold;
    return isPositive ? 'positive' : 'negative';
  };

  if (loading) {
    return <div className="dashboard"><p>Loading dashboard data...</p></div>;
  }

  if (error) {
    return (
      <div className="dashboard">
        <div className="error-banner">
          <p>{error}</p>
          <button onClick={loadData}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Requests</h3>
          <div className="stat-value">{stats.totalRequests.toLocaleString()}</div>
          <div className="stat-change positive">Today</div>
        </div>
        <div className="stat-card">
          <h3>Cache Hit Rate</h3>
          <div className="stat-value">{stats.cacheHitRate.toFixed(1)}%</div>
          <div className={`stat-change ${getChangeIndicator(stats.cacheHitRate, 70)}`}>
            {stats.cacheHitRate > 70 ? 'Good' : 'Low'}
          </div>
        </div>
        <div className="stat-card">
          <h3>Avg Response Time</h3>
          <div className="stat-value">{stats.avgResponseTime.toFixed(0)}ms</div>
          <div className={`stat-change ${getChangeIndicator(50, stats.avgResponseTime)}`}>
            {stats.avgResponseTime < 50 ? 'Fast' : 'Slow'}
          </div>
        </div>
        <div className="stat-card">
          <h3>Active Connections</h3>
          <div className="stat-value">{stats.activeConnections}</div>
          <div className="stat-change">Last 5 minutes</div>
        </div>
        <div className="stat-card">
          <h3>Errors Today</h3>
          <div className="stat-value">{stats.errorsToday}</div>
          <div className={`stat-change ${getChangeIndicator(stats.errorsToday, 5, true)}`}>
            {stats.errorsToday > 5 ? 'High' : 'Normal'}
          </div>
        </div>
        <div className="stat-card">
          <h3>TOR Exit Nodes</h3>
          <div className="stat-value">{stats.torNodesCount.toLocaleString()}</div>
          <div className="stat-change">Updated daily</div>
        </div>
      </div>

      <div className="card">
        <h2>Top Queried IPs (Last 24 Hours)</h2>
        {topIps.length === 0 ? (
          <p>No IP queries recorded yet.</p>
        ) : (
          <ul className="top-ips-list">
            {topIps.map(item => (
              <li key={item.ip} className="top-ip-item">
                <div>
                  <strong>{item.ip}</strong>
                  <span className="country-tag">{item.country}</span>
                </div>
                <div>
                  <strong>{item.count}</strong> requests
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {dbStatus && (
        <div className="card">
          <h2>Database Status</h2>
          <div className="result-section">
            <div className="result-grid">
              <div className="result-item">
                <span className="result-label">MaxMind GeoLite2-City</span>
                <span className="result-value">{dbStatus.maxMindCity}</span>
              </div>
              <div className="result-item">
                <span className="result-label">MaxMind GeoLite2-ASN</span>
                <span className="result-value">{dbStatus.maxMindAsn}</span>
              </div>
              <div className="result-item">
                <span className="result-label">TOR Exit Nodes</span>
                <span className="result-value">{dbStatus.torExitNodes}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Cache Entries</span>
                <span className="result-value">{dbStatus.cacheEntries.toLocaleString()}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;